import type { NavigateFunction } from 'react-router-dom';
import {
  getSessionRecommendationNextAction,
  PalNavigationDection,
  SessionRecommendationNextActionResponse,
  returnFromRemediationApi,
  sessionNavigateApi,
} from '../api/learners';
import {
  PAL_CHALLENGE,
  PAL_DIAGNOSTIC,
  PAL_KNOWLEDGE_CONCEPTS,
  PAL_PRACTICE,
  PAL_VIEW_CONTENT,
} from '../../../routeHandling/RoutesNomenclature';
import { getChallengeApi, startChallengeApi } from '../api/challenge';
import { setQuestionCount, setStreak } from '../reducer/pal.slice';
import { store } from '../../../reduxStore/store';

export interface KnowledgeConceptsNavigationParams {
  sessionId: string;
  moduleId: string;
  navigateToKnowledgeGraph: boolean;
  navigate: NavigateFunction;
  decision?: PalNavigationDection | 'force-update' | 'force-navigate';
  nextConceptId?: string;
  setSessionRecommendationAction?: (
    value: SessionRecommendationNextActionResponse | undefined
  ) => void;
  sessionRecommendedAction?: SessionRecommendationNextActionResponse;
  skipDiagnositc?: boolean;
  isCrossModuleRemediation?: boolean;
  moduleExitFn?: () => void;
  onInvalidSession?: () => void;
}

function isInvalidSessionError(err: unknown): boolean {
  return (err as Error & { status?: number }).status === 422;
}

export async function handleKnowledgeConceptsNavigation({
  sessionId,
  moduleId,
  navigateToKnowledgeGraph,
  navigate,
  decision,
  nextConceptId,
  setSessionRecommendationAction,
  skipDiagnositc,
  isCrossModuleRemediation,
  moduleExitFn,
  onInvalidSession,
}: KnowledgeConceptsNavigationParams): Promise<void> {
  try {
    let decisionToUse = decision;
    let nextConceptIdToUse = nextConceptId;

    let sessionRecommendation:
      | SessionRecommendationNextActionResponse
      | undefined = undefined;
    let useRefresh = false;
    // If the caller didn't provide enough info, fetch it first.

    const navigateToKnowledgeGraphFn = () => {
      navigate(
        `${PAL_KNOWLEDGE_CONCEPTS}/${moduleId}${
          sessionId ? `?sessionId=${sessionId}` : ''
        }`,
        {
          replace: true,
        }
      );
    };

    if (decisionToUse === 'force-navigate') {
      navigateToKnowledgeGraphFn();
      return;
    }

    if (!decisionToUse || !nextConceptIdToUse) {
      sessionRecommendation = await getSessionRecommendationNextAction({
        session_id: sessionId,
      });
      decisionToUse = sessionRecommendation.navigation?.decision;
      nextConceptIdToUse = sessionRecommendation.navigation?.next_concept_id;
    }

    // Per NAVIGATION_DECISIONS.md
    if (decisionToUse === 'promote' && nextConceptIdToUse) {
      if (navigateToKnowledgeGraph) {
        navigateToKnowledgeGraphFn();
        return;
      }
      await sessionNavigateApi(sessionId, {
        concept_id: nextConceptIdToUse,
        decision: decisionToUse,
      });
      useRefresh = true;
    } else if (decisionToUse === 'remediate' && nextConceptIdToUse) {
      if (navigateToKnowledgeGraph) {
        navigateToKnowledgeGraphFn();
        return;
      }
      await sessionNavigateApi(sessionId, {
        concept_id: nextConceptIdToUse,
        decision: decisionToUse,
        is_cross_module: isCrossModuleRemediation,
      });
      useRefresh = true;
    } else if (decisionToUse === 'return_from_remediation') {
      if (navigateToKnowledgeGraph) {
        navigateToKnowledgeGraphFn();
        return;
      }
      await returnFromRemediationApi(sessionId);
      useRefresh = true;
    } else if (nextConceptIdToUse && decisionToUse === 'force-update') {
      await sessionNavigateApi(sessionId, {
        concept_id: nextConceptIdToUse,
      });
      useRefresh = true;
    }

    const refreshed =
      useRefresh || !sessionRecommendation
        ? await getSessionRecommendationNextAction({
            session_id: sessionId,
          })
        : sessionRecommendation;
    if (setSessionRecommendationAction) {
      setSessionRecommendationAction(refreshed);
    }

    if (refreshed.navigation?.decision === 'module_complete') {
      if (navigateToKnowledgeGraph) {
        navigateToKnowledgeGraphFn();
        return;
      }
      if (moduleExitFn) {
        moduleExitFn();
        return;
      }
      navigate(-1);
      return;
    }

    if (
      refreshed.recommended_action === 'start_challenge' ||
      refreshed.recommended_action === 'answer_challenge_question'
    ) {
      let concept_id, external_question_id;
      let questions_answered = 0;
      if (refreshed.recommended_action === 'start_challenge') {
        const res = await startChallengeApi(sessionId);
        concept_id = res.concept_id;
        external_question_id = res.questions[0]?.question_id;
      } else {
        const res = await getChallengeApi(sessionId);
        concept_id = res.concept_id;
        external_question_id = res?.next_question?.question_id;
        questions_answered = res?.progress?.questions_answered || 0;
      }
      if (concept_id) {
        store.dispatch(
          setQuestionCount({
            session_id: sessionId,
            concept_id,
            challenge: questions_answered,
          })
        );
        store.dispatch(
          setStreak({
            session_id: sessionId,
            concept_id,
            challenge: 0,
          })
        );
      }
      navigate(
        `${PAL_CHALLENGE}/${moduleId}/${sessionId}/${concept_id}/${external_question_id}`,
        {
          replace: true,
        }
      );
      return;
    }

    if (refreshed.recommend_diagnostic && !skipDiagnositc) {
      navigate(`${PAL_DIAGNOSTIC}/${moduleId}/${sessionId}`, {
        replace: true,
      });
      return;
    }

    if (refreshed.recommended_action === 'answer_question') {
      navigate(`${PAL_PRACTICE}/${moduleId}/${sessionId}`, {
        replace: true,
      });
      return;
    }

    if (refreshed.recommended_action === 'view_content') {
      navigate(`${PAL_VIEW_CONTENT}/${moduleId}/${sessionId}`, {
        replace: true,
      });
      return;
    }
  } catch (err) {
    if (isInvalidSessionError(err)) {
      onInvalidSession?.();
      return;
    }
    throw err;
  }
}
